import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link, Instagram, Youtube, MessageCircle, Facebook, Twitter, Linkedin, Github, Globe, ExternalLink, User } from 'lucide-react';
import api from '../utils/api';

const ICON_MAP = {
  link: Link,
  instagram: Instagram,
  youtube: Youtube,
  whatsapp: MessageCircle,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  github: Github,
  telegram: MessageCircle,
  website: Globe,
};

const PublicLinkBio = () => {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLinkBio();
  }, [username]);

  const fetchLinkBio = async () => {
    try {
      const response = await api.get(`/linkbio/u/${username}`);
      setData(response.data);
    } catch (err) {
      setError('User not found');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkClick = async (link) => {
    // Track click
    try {
      await api.post(`/linkbio/track-click/${username}/${link.id}`);
    } catch (e) {
      // Ignore tracking errors
    }
    // Open link
    window.open(link.url, '_blank');
  };

  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Link;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="animate-spin w-8 h-8 border-4 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="text-center">
          <User className="w-16 h-16 mx-auto mb-4 text-gray-500" />
          <h1 className="text-2xl font-bold text-white mb-2">User Not Found</h1>
          <p className="text-gray-400">This profile doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const themeColor = data?.theme_color || '#8B5CF6';

  return (
    <div 
      className="min-h-screen py-12 px-6"
      style={{ 
        background: `linear-gradient(135deg, ${themeColor}15 0%, #0a0a0f 50%, ${themeColor}10 100%)` 
      }}
    >
      <div className="max-w-md mx-auto">
        {/* Profile Header */}
        <div className="text-center mb-8">
          {/* Profile Photo */}
          <div className="relative inline-block mb-4">
            {data?.profile_photo ? (
              <img
                src={data.profile_photo}
                alt={data.name}
                className="w-24 h-24 rounded-full object-cover border-4"
                style={{ borderColor: themeColor }}
              />
            ) : (
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold"
                style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}aa)` }}
              >
                {data?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            )}
            {/* Verified badge could go here */}
          </div>

          {/* Name */}
          <h1 className="text-2xl font-bold text-white mb-1">
            {data?.name || data?.username}
          </h1>
          
          {/* Username */}
          <p className="text-gray-400 text-sm mb-3">@{data?.username}</p>

          {/* Bio */}
          {data?.bio && (
            <p className="text-gray-300 text-sm max-w-xs mx-auto">
              {data.bio}
            </p>
          )}
        </div>

        {/* Links */}
        <div className="space-y-3">
          {data?.links?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Link className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No links added yet</p>
            </div>
          ) : (
            data?.links?.map((link) => {
              const IconComp = getIcon(link.icon);
              return (
                <button
                  key={link.id}
                  onClick={() => handleLinkClick(link)}
                  className="w-full group relative overflow-hidden"
                  style={{ 
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  {/* Hover gradient */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: `linear-gradient(135deg, ${themeColor}30, transparent)` }}
                  />
                  
                  <div className="relative flex items-center gap-4 p-4">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: themeColor + '30' }}
                    >
                      <IconComp className="w-5 h-5" style={{ color: themeColor }} />
                    </div>
                    <span className="flex-1 text-white font-medium text-left">
                      {link.title}
                    </span>
                    <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" />
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer / Branding */}
        <div className="mt-12 text-center">
          <a 
            href="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-white text-sm transition-colors"
          >
            <span>Create your own</span>
            <span className="font-bold" style={{ color: themeColor }}>Link in Bio</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default PublicLinkBio;
